import React, { useMemo, useState } from 'react';
import { Loan, Currency, Holding } from '../types';
import { Card, CardHeader, CardContent } from './ui/Card';
import { Button } from './ui/Button';
import { Input, Label, Select } from './ui/Forms';
import { PlusCircle, Calculator, Info } from 'lucide-react';
import { calculateMonthlyPayment, calculateTotalInterest } from '../utils/loans';
import { formatCurrency } from '../utils/currency';
import { CURRENCIES } from '../utils/countries';
interface LoanFormProps {
  onAdd: (loan: Omit<Loan, 'id' | 'createdAt'>) => void;
  partnerNames: [string, string];
  holdings?: Holding[];
  baseCurrency?: Currency;
  initialValues?: {
    name?: string;
    currency?: Currency;
    person?: string;
    totalAmount?: string;
  };
}
export function LoanForm({
  onAdd,
  partnerNames,
  holdings = [],
  baseCurrency = 'USD',
  initialValues
}: LoanFormProps) {
  const [isExistingLoan, setIsExistingLoan] = useState(false);
  const [name, setName] = useState(initialValues?.name ?? '');
  const [person, setPerson] = useState<string>(initialValues?.person ?? 'Joint');
  const [totalAmount, setTotalAmount] = useState(initialValues?.totalAmount ?? '');
  const [depositAmount, setDepositAmount] = useState('0');
  const [currency, setCurrency] = useState<Currency>(initialValues?.currency ?? baseCurrency);
  const [interestRate, setInterestRate] = useState('4.5');
  const [durationMonths, setDurationMonths] = useState('360');
  const [startYear, setStartYear] = useState('0');
  const [repaymentType, setRepaymentType] = useState<Loan['repaymentType']>('annuity');
  const [depositFundedFromHoldingId, setDepositFundedFromHoldingId] = useState<string>('none');
  const liquidHoldings = useMemo(() => {
    return holdings.filter(h => h.type === 'Liquid' && h.amount > 0);
  }, [holdings]);
  // Compute preview values
  const previewLoan = useMemo<Loan>(() => {
    return {
      id: 'preview',
      name: name || 'Preview',
      person,
      totalAmount: Number(totalAmount) || 0,
      depositAmount: Number(depositAmount) || 0,
      interestRate: Number(interestRate) || 0,
      durationMonths: Number(durationMonths) || 0,
      startYear: Number(startYear) || 0,
      currency,
      repaymentType,
      isActive: true,
      createdAt: new Date().toISOString()
    };
  }, [name, person, totalAmount, depositAmount, interestRate, durationMonths, startYear, currency, repaymentType]);
  const monthlyPayment = calculateMonthlyPayment(previewLoan);
  const totalInterest = calculateTotalInterest(previewLoan);
  const principal = Math.max(0, previewLoan.totalAmount - previewLoan.depositAmount);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !totalAmount || isNaN(Number(totalAmount))) return;
    if (!durationMonths || isNaN(Number(durationMonths))) return;
    const finalDepositAmount = isExistingLoan ? 0 : Number(depositAmount) || 0;
    const finalStartYear = isExistingLoan ? 0 : Number(startYear) || 0;
    const finalFundedFrom = isExistingLoan ? undefined : depositFundedFromHoldingId !== 'none' ? depositFundedFromHoldingId : undefined;
    onAdd({
      name,
      person,
      totalAmount: Number(totalAmount),
      depositAmount: finalDepositAmount,
      interestRate: Number(interestRate) || 0,
      durationMonths: Number(durationMonths),
      startYear: finalStartYear,
      currency,
      repaymentType,
      isActive: true,
      depositFundedFromHoldingId: finalFundedFrom
    });
    setName('');
    setTotalAmount('');
    setDepositAmount('0');
    setDepositFundedFromHoldingId('none');
  };
  return <Card data-id="element-856">
      <CardHeader title="Add Loan / Debt" subtitle="Mortgages, car loans, student debt, etc." data-id="element-857" />
      <CardContent data-id="element-858">
        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-859">
          <div className="flex bg-slate-100 p-1 rounded-lg mb-2" data-id="element-860">
            <button type="button" className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors ${!isExistingLoan ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => setIsExistingLoan(false)} data-id="element-861">
              🆕 New Loan
            </button>
            <button type="button" className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors ${isExistingLoan ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => setIsExistingLoan(true)} data-id="element-862">
              🏦 Existing Loan
            </button>
          </div>

          {isExistingLoan && <div className="bg-blue-50 border border-blue-100 text-blue-700 p-3 rounded-lg text-sm flex gap-2 items-start" data-id="element-863">
              <Info className="w-4 h-4 mt-0.5 shrink-0" data-id="element-864" />
              <p data-id="element-865">
                Enter the current outstanding balance and remaining term. The
                projection engine will calculate payments from today.
              </p>
            </div>}

          <div className="grid grid-cols-2 gap-4" data-id="element-866">
            <div className="col-span-2 sm:col-span-1" data-id="element-867">
              <Label data-id="element-868">Loan Name</Label>
              <Input placeholder="e.g. Mortgage, Auto Loan" value={name} onChange={e => setName(e.target.value)} required data-id="element-869" />
            </div>
            <div className="col-span-2 sm:col-span-1" data-id="element-870">
              <Label data-id="element-871">Owner</Label>
              <Select value={person} onChange={e => setPerson(e.target.value)} options={[{
              value: 'Joint',
              label: 'Joint (50/50)'
            }, {
              value: partnerNames[0],
              label: partnerNames[0]
            }, {
              value: partnerNames[1],
              label: partnerNames[1]
            }]} data-id="element-872" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4" data-id="element-873">
            <div data-id="element-874">
              <Label data-id="element-875">
                {isExistingLoan ? 'Outstanding Balance' : 'Total Property/Asset Value'}
              </Label>
              <Input type="number" min="0" step="1" placeholder="0" value={totalAmount} onChange={e => setTotalAmount(e.target.value)} required data-id="element-876" />
            </div>
            {!isExistingLoan && <div data-id="element-877">
                <Label data-id="element-878">Down Payment / Deposit</Label>
                <Input type="number" min="0" step="1" placeholder="0" value={depositAmount} onChange={e => setDepositAmount(e.target.value)} data-id="element-879" />
              </div>}
          </div>

          {/* Fund Deposit From — shown when deposit > 0 and liquid holdings exist */}
          {!isExistingLoan && (Number(depositAmount) || 0) > 0 && liquidHoldings.length > 0 && <div data-id="element-880">
                <Label data-id="element-881">Fund Deposit From</Label>
                <Select searchable value={depositFundedFromHoldingId} onChange={e => setDepositFundedFromHoldingId(e.target.value)} options={[{
            value: 'none',
            label: "Don't deduct from holdings (manual / external)"
          }, ...liquidHoldings.map(h => ({
            value: h.id,
            label: `${h.name} (${formatCurrency(h.amount, h.currency)})`
          }))]} data-id="element-882" />
                {depositFundedFromHoldingId !== 'none' && <p className="text-xs text-amber-600 mt-1.5" data-id="element-883">
                    ⚠ {formatCurrency(Number(depositAmount) || 0, currency)}{' '}
                    will be deducted from this holding when the loan is added.
                  </p>}
              </div>}

          <div className="grid grid-cols-2 gap-4" data-id="element-884">
            <div data-id="element-885">
              <Label data-id="element-886">Currency</Label>
              <Select searchable value={currency} onChange={e => setCurrency(e.target.value as Currency)} options={CURRENCIES.map(c => ({
              value: c.code,
              label: `${c.flag} ${c.code} (${c.symbol})`
            }))} data-id="element-887" />
            </div>
            <div data-id="element-888">
              <Label data-id="element-889">Annual Interest Rate (%)</Label>
              <Input type="number" min="0" step="0.01" placeholder="4.5" value={interestRate} onChange={e => setInterestRate(e.target.value)} required data-id="element-890" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4" data-id="element-891">
            <div data-id="element-892">
              <Label data-id="element-893">
                {isExistingLoan ? 'Remaining Months' : 'Duration (Months)'}
              </Label>
              <div className="relative" data-id="element-894">
                <Input type="number" min="1" step="1" placeholder="360" value={durationMonths} onChange={e => setDurationMonths(e.target.value)} required data-id="element-895" />
                <span className="absolute right-3 top-2 text-xs text-slate-400 pointer-events-none" data-id="element-896">
                  {durationMonths ? `${(Number(durationMonths) / 12).toFixed(1)} yrs` : ''}
                </span>
              </div>
            </div>
            {!isExistingLoan && <div data-id="element-897">
                <Label data-id="element-898">Start Year (from now)</Label>
                <Input type="number" min="0" step="1" placeholder="0" value={startYear} onChange={e => setStartYear(e.target.value)} required data-id="element-899" />
              </div>}
          </div>

          <div data-id="element-900">
            <Label data-id="element-901">Repayment Type</Label>
            <Select value={repaymentType} onChange={e => setRepaymentType(e.target.value as Loan['repaymentType'])} options={[{
            value: 'annuity',
            label: 'Annuity (Fixed Monthly)'
          }, {
            value: 'linear',
            label: 'Linear (Decreasing)'
          }, {
            value: 'interest-only',
            label: 'Interest Only'
          }]} data-id="element-902" />
          </div>

          {/* Preview Summary */}
          {principal > 0 && Number(durationMonths) > 0 && <div className="mt-6 p-4 bg-slate-50 rounded-xl border border-slate-100 flex flex-col gap-3" data-id="element-903">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-1" data-id="element-904">
                <Calculator className="w-4 h-4 text-blue-500" data-id="element-905" />
                Loan Summary Preview
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-906">
                <div data-id="element-907">
                  <p className="text-slate-500 text-xs mb-0.5" data-id="element-908">
                    {isExistingLoan ? 'Outstanding Balance' : 'Principal Amount'}
                  </p>
                  <p className="font-medium text-slate-900" data-id="element-909">
                    {formatCurrency(principal, currency)}
                  </p>
                </div>
                <div data-id="element-910">
                  <p className="text-slate-500 text-xs mb-0.5" data-id="element-911">
                    Est. Monthly Payment
                  </p>
                  <p className="font-bold text-blue-700" data-id="element-912">
                    {formatCurrency(monthlyPayment, currency)}
                  </p>
                </div>
                <div data-id="element-913">
                  <p className="text-slate-500 text-xs mb-0.5" data-id="element-914">
                    Total Interest
                  </p>
                  <p className="font-medium text-rose-600" data-id="element-915">
                    {formatCurrency(totalInterest, currency)}
                  </p>
                </div>
                <div data-id="element-916">
                  <p className="text-slate-500 text-xs mb-0.5" data-id="element-917">Total Cost</p>
                  <p className="font-medium text-slate-900" data-id="element-918">
                    {formatCurrency(principal + totalInterest, currency)}
                  </p>
                </div>
              </div>
            </div>}

          <Button type="submit" className="w-full mt-4" data-id="element-919">
            <PlusCircle className="w-4 h-4 mr-2" data-id="element-920" />
            Add Loan
          </Button>
        </form>
      </CardContent>
    </Card>;
}